import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import moment from 'moment';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LogThExport, TH_LIST_METRICS } from 'src/app/conf/thermo';
import * as DeviceThActions from 'src/app/store/device-th/device-th.actions';
import { minDateLogs } from 'src/app/conf/log';
import { selectLoadedThLogExp, selectLoadingThLogExp, selectLogThExport } from 'src/app/store/device-th/device-th.selector';

@Component({
	templateUrl: './thermo-export-log.dialog.component.html',
})

export class ThermoExportLogDialogComponent implements OnInit, OnDestroy {

	loading$: Observable<boolean | undefined>;
	loadingSub: Subscription | undefined;
	loaded$: Observable<boolean | undefined>;
	loadedSub: Subscription | undefined;
	logThExport$: Observable<LogThExport | undefined>;
	logThExportSub: Subscription | undefined;

	loading: boolean;
	loaded: boolean;
	logThExport: LogThExport;
	listMetrics = TH_LIST_METRICS;

	exportForm: FormGroup;
	MAX_MD_WIDTH = 1120;

	minDate: Date = new Date();
	start: Date = new Date(moment().startOf('day').add(-7, 'days').toDate());

	// design
	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		private translate: TranslateService,
		public config: DynamicDialogConfig,
		private http: HttpClient,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.loading$ = this.store.select(selectLoadingThLogExp);
		this.loaded$ = this.store.select(selectLoadedThLogExp);
		this.logThExport$ = this.store.select(selectLogThExport);
	}

	ngOnInit(): void {
		this.minDate = minDateLogs();
		this.loadingSub = this.loading$.subscribe(loading => {
			this.loading = loading;
		});
		this.loadedSub = this.loaded$.subscribe(loaded => {
			this.loaded = loaded;
		});
		this.logThExportSub = this.logThExport$.subscribe(urlLog => {
			this.logThExport = urlLog;
			if (this.logThExport !== undefined && this.logThExport.FileType !== '' && this.logThExport.Url !== '') {
				this.downloadFile();
			}
		});
		this.buildForm();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.loadingSub?.unsubscribe();
		this.loadedSub?.unsubscribe();
		this.logThExportSub?.unsubscribe();
	}

	downloadFile(): void {
		window.open(this.logThExport.Url);
		this.closeDialog();
	}


	buildForm(): void {
		const dateEnd = new Date();
		//MAX 365 giorni
		//MIN 7 giorni
		//MIN GENNAIO 2019
		this.exportForm = new FormGroup({
			'Metrics': new FormControl(null, Validators.required),
			'startDate': new FormControl(this.start, Validators.required),
			'startTime': new FormControl(this.getTime(this.start), Validators.required),
			'endDate': new FormControl(dateEnd, Validators.required),
			'endTime': new FormControl(this.getTime(dateEnd), Validators.required),
		});
		this.exportForm.addValidators(CustomValidators.checkCorrectDateSequence('startDate', 'startTime', 'endDate', 'endTime'));
	}

	get f(): any {
		return this.exportForm.controls;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.exportForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.exportForm.get(key).errors;
			if (controlErrors != null) {
				Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				});
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		const listMet = [];
		for (const metric of this.f.Metrics.value) {
			listMet.push(metric.Value);
		}

		const startDate = moment(this.f.startDate.value).format('YYYY-MM-DD');
		let startTime = ''
		if (typeof this.f.startTime.value === 'string') {
			startTime = 'T' + this.f.startTime.value + ':00';
		} else {
			startTime = moment(this.f.startTime.value).format('THH:mm:00');
		}
		const endDate = moment(this.f.endDate.value).format('YYYY-MM-DD');
		let endTime = ''
		if (typeof this.f.endTime.value === 'string') {
			endTime = 'T' + this.f.endTime.value + ':00';
		} else {
			endTime = moment(this.f.endTime.value).format('THH:mm:00');
		}

		this.store.dispatch(DeviceThActions.getLogThExport({
			startDate: moment(startDate + startTime).toDate(),
			endDate: moment(endDate + endTime).toDate(),
			metrics: listMet,
		}));
	}


	getTime(date: Date): string {
		return moment(date).format('HH:mm');
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicAutocomplete(): string {
		return StandardDynamicComponent.dynamicAutocomplete(this.screenWidth);
	}

	dynamicInputShortTextWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-30rem');
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	dynamicInputTextFormat(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	newShowErrors(type: ThermoExportLogDialogComponent["f"], classType: string) {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: ThermoExportLogDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
	dynamicInputTextWidthMultiselect(): string {
		return this.screenWidth <= 867 ? 'w-full h-2rem' : 'w-35rem h-2rem';
	}

	dynamicInputTextFormatMultiselect(classType: string): string {
		switch (classType) {
			case 'borderRow':
				if (this.screenWidth <= 867) {
					return "row";
				}
				return "";
			case 'borderInput':
				if (this.screenWidth <= 867) {
					return "col-12 w-full p-0";
				}
				return "";
			case 'label':
				if (this.screenWidth <= 867) {
					return "col-12 p-1";
				}
				return "";
			case 'inputText':
				if (this.screenWidth <= this.MAX_MD_WIDTH) {
					if (this.screenWidth <= 867) {
						return "w-full h-2rem"
					}
					return "w-35rem h-2rem";
				}
				return "w-35rem h-2rem";
			default:
				return "";
		}
	}
}
